import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Seo, SITE_URL } from "@/components/Seo";
import NotFound from "@/pages/NotFound";
import { useProtocolOverview, entityTrack, buildProtocolJsonLd } from "@/lib/entities";
import { EntitySelectionProvider } from "@/components/entity/EntitySelectionContext";
import { EntityHero } from "@/components/entity/EntityHero";
import { ProtocolNetwork } from "@/components/entity/ProtocolNetwork";
import { EntityDexActivity } from "@/components/entity/EntityDexActivity";
import { TVLIntelligence } from "@/components/entity/TVLIntelligence";
import { ChainDistribution } from "@/components/entity/ChainDistribution";
import { AssociatedToken } from "@/components/entity/AssociatedToken";
import { WhatChanged } from "@/components/entity/WhatChanged";
import { YieldIntelligence } from "@/components/entity/YieldIntelligence";
import { EntityCoverage } from "@/components/entity/EntityCoverage";
import { EntityGovernanceModule } from "@/components/entity/modules/EntityGovernanceModule";
import { EntitySecurityModule } from "@/components/entity/modules/EntitySecurityModule";
import { EntityRegulationModule } from "@/components/entity/modules/EntityRegulationModule";
import { EntityLayerIndexModule } from "@/components/entity/modules/EntityLayerIndexModule";
import { RelatedEntities } from "@/components/entity/RelatedEntities";
import { EntityIntelligenceClose } from "@/components/entity/EntityIntelligenceClose";

/* Entity Intelligence Page — Figma is the visual contract, the Entity Data
   Service is the data contract. Reusable protocol shell; Aave is the flagship
   reference. Non-live entities render but stay noindex + out of the sitemap. */

const Skeleton = () => (
  <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-[30px] pb-[60px]" data-testid="protocol-skeleton">
    <div className="grid gap-[40px] lg:grid-cols-2 items-start">
      <div className="space-y-[26px]">
        <div className="h-[14px] w-[240px] rounded bg-white/[0.04] animate-pulse" />
        <div className="h-[78px] w-[300px] rounded bg-white/[0.05] animate-pulse" />
        <div className="h-[72px] w-full max-w-[500px] rounded bg-white/[0.03] animate-pulse" />
        <div className="h-[64px] w-full max-w-[560px] rounded bg-white/[0.03] animate-pulse" />
      </div>
      <div className="h-[420px] rounded-[22px] bg-white/[0.02] border border-[#171a26] animate-pulse" />
    </div>
    <div className="h-[470px] rounded-[22px] bg-white/[0.02] border border-[#171a26] animate-pulse mt-[74px]" />
  </div>
);

export default function ProtocolPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, isError } = useProtocolOverview(slug);
  const viewFired = useRef<string | null>(null);

  useEffect(() => {
    const id = data?.identity?.entity_id;
    if (id && viewFired.current !== id) {
      viewFired.current = id; // once per entity — guards StrictMode/refetch re-runs
      entityTrack("entity_page_view", id);
    }
  }, [data?.identity?.entity_id]);

  if (isError) return <NotFound />;

  const id = data?.identity;
  const isDex = id?.profile === "dex";
  const trackedYears = id?.tracked_since
    ? Math.max(1, Math.floor((Date.now() - new Date(id.tracked_since).getTime()) / (365.25 * 86400000)))
    : null;

  return (
    <div className="relative overflow-hidden bg-[#030305]" data-testid="protocol-page">
      {id && (
        <Seo
          title={`${id.name} Protocol Intelligence — ${id.profile === "dex" ? "Volume, Markets" : "TVL, Deployments"} & Coverage`}
          description={id.description || `${id.name} protocol intelligence: live ${id.profile === "dex" ? "trading volume" : "TVL"}, deployment topology, yields and canonical NewsLayer coverage.`}
          path={`/protocols/${id.slug}`}
          noindex={!id.live}
          jsonLd={buildProtocolJsonLd(id, SITE_URL)}
        />
      )}
      {/* Ambient fields (Figma) */}
      <div aria-hidden className="absolute pointer-events-none top-[60px] right-[4%] w-[620px] h-[620px] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(110,43,245,0.18) 0%, rgba(51,20,102,0.08) 55%, rgba(0,0,0,0) 100%)" }} />
      <div aria-hidden className="absolute pointer-events-none top-[230px] right-[-4%] w-[420px] h-[420px] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(33,212,237,0.075) 0%, rgba(0,0,0,0) 88%)" }} />

      {isLoading || !data ? (
        <Skeleton />
      ) : !id ? (
        <NotFound />
      ) : (
        <EntitySelectionProvider>
          <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-[24px] md:pt-[30px] pb-[40px] md:pb-[56px]">
            <div className="grid gap-[44px] lg:gap-[56px] lg:grid-cols-[minmax(0,47fr)_minmax(0,53fr)] items-start">
              <EntityHero overview={data} />
              <ProtocolNetwork overview={data} />
            </div>

            <div className="h-px bg-[#222234] mt-[48px] md:mt-[60px]" aria-hidden />

            {id.profile === "dex" && data.dex_activity && (
              <section className="mt-[52px] md:mt-[70px]" data-testid="dex-activity-section">
                <p className="nl-geist text-[8px] md:text-[10px] leading-[12px] md:leading-[14px] font-semibold tracking-[0.08em] md:tracking-[1px] text-[#22d3ee] uppercase">
                  TRADING INTELLIGENCE
                </p>
                <h2 className="nl-sg text-[25px] leading-[31px] md:text-[34px] md:leading-[40px] font-medium tracking-[-0.005em] text-white mt-[8px]">
                  DEX Activity
                </h2>
                <p className="nl-geist text-[13px] md:text-[15px] leading-[20px] md:leading-[23px] text-[#b9b9c7] mt-[8px] max-w-[720px]">
                  Where and how {id.name} trading happens — the provider's real daily volume
                  series, never interpolated.
                </p>
                <div className="mt-[22px] min-w-0">
                  <EntityDexActivity overview={data} />
                </div>
              </section>
            )}

            <section className="grid lg:grid-cols-[minmax(0,66fr)_minmax(0,34fr)] lg:grid-rows-[auto_1fr] lg:gap-x-[36px] mt-[52px] md:mt-[70px]"
              data-testid="tvl-chain-section">
              <div className="lg:col-start-1 lg:row-start-1">
                <p className="nl-geist text-[8px] md:text-[10px] leading-[12px] md:leading-[14px] font-semibold tracking-[0.08em] md:tracking-[1px] text-[#8b5cff] uppercase">
                  {isDex ? "SUPPORTING CONTEXT" : "PROTOCOL INTELLIGENCE"}
                </p>
                <h2 className="nl-sg text-[25px] leading-[31px] md:text-[34px] md:leading-[40px] font-medium tracking-[-0.005em] text-white mt-[8px]">
                  TVL Intelligence
                </h2>
                <p className="nl-geist text-[13px] md:text-[15px] leading-[20px] md:leading-[23px] text-[#b9b9c7] mt-[8px] max-w-[720px]">
                  {isDex
                    ? `Liquidity deposited in ${id.name} pools — the capital base behind trading activity. TVL is distinct from volume: it measures depth, not flow.`
                    : `${trackedYears ? `${trackedYears === 1 ? "A year" : `${trackedYears} years`} of real protocol history` : "Real protocol history"} with range-aware interaction and NewsLayer-owned snapshot provenance.`}
                </p>
              </div>
              <div className="mt-[22px] min-w-0 lg:col-start-1 lg:row-start-2">
                <TVLIntelligence slug={id.slug} entityId={id.entity_id} />
              </div>
              <div className="mt-[44px] lg:mt-0 lg:col-start-2 lg:row-start-1">
                <p className="nl-geist text-[8px] md:text-[10px] leading-[12px] md:leading-[14px] font-semibold tracking-[0.08em] md:tracking-[1px] text-[#22d3ee] uppercase">
                  DEPLOYMENT COMPOSITION
                </p>
                <h2 className="nl-sg text-[25px] leading-[31px] md:text-[30px] md:leading-[36px] font-medium tracking-[-0.005em] text-white mt-[8px]">
                  Chain Distribution
                </h2>
                <p className="nl-geist text-[13px] md:text-[15px] leading-[20px] md:leading-[23px] text-[#b9b9c7] mt-[8px]">
                  Where {id.name} TVL is deployed now.
                </p>
              </div>
              <div className="mt-[22px] min-w-0 lg:col-start-2 lg:row-start-2">
                <ChainDistribution overview={data} />
              </div>
            </section>

            <div className="mt-[44px] md:mt-[54px]">
              <AssociatedToken overview={data} />
            </div>

            <div className="mt-[52px] md:mt-[70px] lg:mt-[90px]">
              <WhatChanged overview={data} />
            </div>

            <div className="mt-[52px] md:mt-[70px] lg:mt-[90px]">
              <YieldIntelligence overview={data} />
            </div>

            <div className="mt-[52px] md:mt-[70px] lg:mt-[90px]">
              <EntityCoverage overview={data} />
            </div>

            <div className="mt-[52px] md:mt-[70px] lg:mt-[90px] space-y-[22px] md:space-y-[28px]"
              data-testid="entity-intelligence-modules">
              <EntityGovernanceModule overview={data} />
              <EntitySecurityModule overview={data} />
              <EntityRegulationModule overview={data} />
              <EntityLayerIndexModule overview={data} />
            </div>

            <div className="mt-[52px] md:mt-[70px] lg:mt-[90px]">
              <RelatedEntities overview={data} />
            </div>

            <div className="mt-[52px] md:mt-[70px] lg:mt-[90px]">
              <EntityIntelligenceClose slug={id.slug} />
            </div>

            <p className="nl-mono text-[9px] md:text-[10px] leading-[13px] font-medium text-[color:var(--nl-text-faint)] mt-[40px] md:mt-[50px] uppercase">
              NEWSLAYER · NEWS FOR THE ONCHAIN ECONOMY
            </p>
          </div>
        </EntitySelectionProvider>
      )}
    </div>
  );
}
